import React, { useEffect, useState } from "react";
import { Table, Button, Modal, Form, Input, InputNumber, Select, Space, message, Popconfirm } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import useRequest from "../../hooks/useRequest";
import apiPath from "../../constants/apiPath";
import EmptyState from "../../components/ui/EmptyState";

// Types / policies tables, shown as tabs of the Leave page. section: "types" | "policies".
const LeaveSettings = ({ section = "types" }) => {
  const { request } = useRequest();
  const [types, setTypes] = useState([]);
  const [policies, setPolicies] = useState([]);

  const [typeModalOpen, setTypeModalOpen] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [typeForm] = Form.useForm();

  const [policyModalOpen, setPolicyModalOpen] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [policyForm] = Form.useForm();

  const fetchTypes = () => {
    request({ url: apiPath.leaveTypes, method: "GET", onSuccess: (res) => setTypes(res?.data?.data || []) });
  };
  const fetchPolicies = () => {
    request({ url: apiPath.leavePolicies, method: "GET", onSuccess: (res) => setPolicies(res?.data?.data || []) });
  };

  useEffect(() => {
    fetchTypes();
    fetchPolicies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Leave Types ----
  const openCreateType = () => {
    setEditingType(null);
    typeForm.resetFields();
    setTypeModalOpen(true);
  };
  const openEditType = (record) => {
    setEditingType(record);
    typeForm.setFieldsValue(record);
    setTypeModalOpen(true);
  };
  const onSubmitType = () => {
    typeForm.validateFields().then((values) => {
      const url = editingType ? `${apiPath.leaveTypes}/${editingType._id}` : apiPath.leaveTypes;
      const method = editingType ? "PUT" : "POST";
      request({
        url,
        method,
        data: values,
        onSuccess: () => {
          message.success(editingType ? "Leave type updated" : "Leave type created");
          setTypeModalOpen(false);
          fetchTypes();
        },
      });
    });
  };
  const onDeleteType = (record) => {
    request({
      url: `${apiPath.leaveTypes}/${record._id}`,
      method: "DELETE",
      onSuccess: () => {
        message.success("Leave type deleted");
        fetchTypes();
      },
    });
  };

  // ---- Leave Policies ----
  const openCreatePolicy = () => {
    setEditingPolicy(null);
    policyForm.resetFields();
    setPolicyModalOpen(true);
  };
  const openEditPolicy = (record) => {
    setEditingPolicy(record);
    policyForm.setFieldsValue({ ...record, leaveType: record.leaveType?._id });
    setPolicyModalOpen(true);
  };
  const onSubmitPolicy = () => {
    policyForm.validateFields().then((values) => {
      const url = editingPolicy ? `${apiPath.leavePolicies}/${editingPolicy._id}` : apiPath.leavePolicies;
      const method = editingPolicy ? "PUT" : "POST";
      request({
        url,
        method,
        data: values,
        onSuccess: () => {
          message.success(editingPolicy ? "Leave policy updated" : "Leave policy created");
          setPolicyModalOpen(false);
          fetchPolicies();
        },
      });
    });
  };
  const onDeletePolicy = (record) => {
    request({
      url: `${apiPath.leavePolicies}/${record._id}`,
      method: "DELETE",
      onSuccess: () => {
        message.success("Leave policy deleted");
        fetchPolicies();
      },
    });
  };

  const typeColumns = [
    { title: "Name", dataIndex: "name" },
    { title: "Description", dataIndex: "description" },
    { title: "Default Balance", dataIndex: "defaultBalance" },
    {
      title: "Actions",
      render: (_, record) => (
        <Space>
          <Button icon={<EditOutlined />} size="small" onClick={() => openEditType(record)} />
          <Popconfirm title="Delete this leave type?" onConfirm={() => onDeleteType(record)}>
            <Button icon={<DeleteOutlined />} size="small" danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const policyColumns = [
    { title: "Name", dataIndex: "name" },
    { title: "Leave Type", dataIndex: ["leaveType", "name"] },
    { title: "Annual Allowance", dataIndex: "annualAllowance" },
    {
      title: "Actions",
      render: (_, record) => (
        <Space>
          <Button icon={<EditOutlined />} size="small" onClick={() => openEditPolicy(record)} />
          <Popconfirm title="Delete this leave policy?" onConfirm={() => onDeletePolicy(record)}>
            <Button icon={<DeleteOutlined />} size="small" danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const typesPane = (
    <>
      <Space style={{ marginBottom: 16, width: "100%", justifyContent: "flex-end" }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreateType}>
          Add Leave Type
        </Button>
      </Space>
      <Table
        rowKey="_id"
        columns={typeColumns}
        dataSource={types}
        pagination={{ pageSize: 10 }}
        locale={{ emptyText: <EmptyState message="No leave types yet" actionLabel="Add Leave Type" onAction={openCreateType} /> }}
      />
    </>
  );
  const policiesPane = (
    <>
      <Space style={{ marginBottom: 16, width: "100%", justifyContent: "flex-end" }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreatePolicy}>
          Add Leave Policy
        </Button>
      </Space>
      <Table
        rowKey="_id"
        columns={policyColumns}
        dataSource={policies}
        pagination={{ pageSize: 10 }}
        locale={{ emptyText: <EmptyState message="No leave policies yet" actionLabel="Add Leave Policy" onAction={openCreatePolicy} /> }}
      />
    </>
  );

  return (
    <>
      {section === "policies" ? policiesPane : typesPane}

      <Modal title={editingType ? "Edit Leave Type" : "Add Leave Type"} open={typeModalOpen} onCancel={() => setTypeModalOpen(false)} onOk={onSubmitType} destroyOnClose>
        <Form layout="vertical" form={typeForm}>
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input placeholder="e.g. Casual, Sick, Annual" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="defaultBalance" label="Default Balance" initialValue={0}>
            <InputNumber style={{ width: "100%" }} min={0} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal title={editingPolicy ? "Edit Leave Policy" : "Add Leave Policy"} open={policyModalOpen} onCancel={() => setPolicyModalOpen(false)} onOk={onSubmitPolicy} destroyOnClose>
        <Form layout="vertical" form={policyForm}>
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="leaveType" label="Leave Type" rules={[{ required: true }]}>
            <Select options={types.map((t) => ({ value: t._id, label: t.name }))} />
          </Form.Item>
          <Form.Item name="annualAllowance" label="Annual Allowance" rules={[{ required: true }]}>
            <InputNumber style={{ width: "100%" }} min={0} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default LeaveSettings;
